import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Chip,
  Grid,
  InputAdornment,
  SelectChangeEvent,
  useTheme,
  Tooltip,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import { useSnackbar } from 'notistack';
import api from '../api';

interface Employee {
  id: number;
  fullName: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  organizationId: number;
  organization: { id: number; name: string };
  _count: { assignments: number };
}

interface Organization {
  id: number;
  name: string;
}

const STATUS_OPTIONS = ['Работает', 'Уволен'];

const emptyForm = {
  fullName: '',
  position: '',
  email: '',
  phone: '',
  status: 'Работает',
  organizationId: '' as number | '',
};

export default function EmployeesPage() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Employee | null>(null);
  const [dismissConfirm, setDismissConfirm] = useState<Employee | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const statusDisplay = useMemo(() => (s: string) =>
    s === 'Уволен' ? t('employees.statusDismissed') : t('employees.statusWorking'),
  [t]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const [empRes, orgRes] = await Promise.all([
        api.get(`/employees?${params}`),
        api.get('/organizations'),
      ]);
      setEmployees(empRes.data);
      setOrgs(orgRes.data);
    } catch (err: any) {
      enqueueSnackbar(t('employees.errorLoading'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, enqueueSnackbar, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!form.fullName.trim()) errors.fullName = t('employees.fullNameRequired');
    if (!form.organizationId) errors.organizationId = t('employees.organizationRequired');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = t('employees.invalidEmail');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        fullName: form.fullName,
        position: form.position || null,
        email: form.email || null,
        phone: form.phone || null,
        status: form.status,
        organizationId: form.organizationId as number,
      };
      if (editingId) {
        await api.put(`/employees/${editingId}`, payload);
        enqueueSnackbar(t('employees.snackbarUpdated'), { variant: 'success' });
      } else {
        await api.post('/employees', payload);
        enqueueSnackbar(t('employees.snackbarCreated'), { variant: 'success' });
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchData();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (emp: Employee) => {
    setEditingId(emp.id);
    setForm({
      fullName: emp.fullName,
      position: emp.position || '',
      email: emp.email || '',
      phone: emp.phone || '',
      status: emp.status || 'Работает',
      organizationId: emp.organizationId,
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/employees/${deleteConfirm.id}`);
      enqueueSnackbar(t('employees.snackbarDeleted'), { variant: 'success' });
      setDeleteConfirm(null);
      fetchData();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  const handleDismiss = async () => {
    if (!dismissConfirm) return;
    try {
      await api.put(`/employees/${dismissConfirm.id}`, {
        fullName: dismissConfirm.fullName,
        position: dismissConfirm.position || null,
        email: dismissConfirm.email || null,
        phone: dismissConfirm.phone || null,
        status: 'Уволен',
        organizationId: dismissConfirm.organizationId,
      });
      enqueueSnackbar(t('employees.snackbarDismissed'), { variant: 'success' });
      setDismissConfirm(null);
      fetchData();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  if (loading && employees.length === 0) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }

  const activeFilters = Number(search.trim() !== '') + Number(statusFilter !== 'all');

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #34d399 20%, #0ea5e9 80%)'
                : 'linear-gradient(135deg, #10b981 20%, #0ea5e9 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(52,211,153,0.55)'
                : '0 8px 20px -8px rgba(16,185,129,0.4)',
            }}
          >
            <PeopleIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('employees.intro')}: <strong style={{ color: 'var(--text)' }}>{employees.length}</strong> {t('employees.employees', { count: employees.length })}
            {activeFilters > 0 && (
              <> · {t('employees.filters')}: <strong style={{ color: 'var(--primary)' }}>{activeFilters}</strong></>
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingId(null); setForm(emptyForm); setFormErrors({}); setDialogOpen(true); }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1.5,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              placeholder={t('employees.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('common.status')}
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{statusDisplay(s)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            {activeFilters > 0 && (
              <Button
                size="small"
                fullWidth
                onClick={() => { setSearch(''); setStatusFilter('all'); }}
              >
                {t('employees.clearFilters')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('employees.tableFullName')}</TableCell>
                <TableCell>{t('employees.tablePosition')}</TableCell>
                <TableCell>{t('employees.tableEmail')}</TableCell>
                <TableCell>{t('employees.tablePhone')}</TableCell>
                <TableCell>{t('employees.tableOrganization')}</TableCell>
                <TableCell>{t('employees.tableStatus')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell>
                </TableRow>
              ) : employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                    {t('employees.noEmployees')}
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{emp.fullName}</TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>{emp.position || '—'}</TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>
                      {emp.email ? (
                        <a
                          href={`mailto:${emp.email}`}
                          style={{ color: 'var(--primary)', textDecoration: 'none' }}
                        >
                          {emp.email}
                        </a>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>{emp.phone || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={emp.organization.name}
                        size="small"
                        sx={{
                          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)',
                          border: 'none',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusDisplay(emp.status || 'Работает')}
                        size="small"
                        color={emp.status === 'Уволен' ? 'error' : 'success'}
                        sx={{
                          background:
                            emp.status === 'Уволен'
                              ? isDark ? 'rgba(248,113,113,0.15)' : 'rgba(239,68,68,0.12)'
                              : isDark ? 'rgba(52,211,153,0.15)' : 'rgba(16,185,129,0.12)',
                          color: 'inherit',
                          border: 'none',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {emp.status === 'Работает' && (
                        <Tooltip
                          title={
                            emp._count.assignments > 0
                              ? t('employees.dismissDisabled', { count: emp._count.assignments })
                              : t('employees.dismissButton')
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => setDismissConfirm(emp)}
                              disabled={emp._count.assignments > 0}
                              aria-label={t('employees.dismissButton')}
                            >
                              <PersonOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <IconButton size="small" onClick={() => handleEdit(emp)} aria-label={t('common.edit')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Tooltip
                        title={
                          emp._count.assignments > 0
                            ? t('employees.deleteDisabled', { count: emp._count.assignments })
                            : t('common.delete')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirm(emp)}
                            disabled={emp._count.assignments > 0}
                            aria-label={t('common.delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('employees.editEmployee') : t('employees.addEmployee')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('employees.formFullName')}
            required
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            error={!!formErrors.fullName}
            helperText={formErrors.fullName}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label={t('employees.formPosition')}
            value={form.position}
            onChange={(e) => setForm({ ...form, position: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('employees.formEmail')}
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={!!formErrors.email}
            helperText={formErrors.email}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('employees.formPhone')}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('employees.formStatus')}</InputLabel>
            <Select
              value={form.status}
              label={t('employees.formStatus')}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{statusDisplay(s)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }} error={!!formErrors.organizationId}>
            <InputLabel>{t('employees.formOrganization')}</InputLabel>
            <Select
              value={form.organizationId}
              label={t('employees.formOrganization')}
              onChange={(e) => setForm({ ...form, organizationId: e.target.value as number })}
            >
              {orgs.map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('employees.deleteEmployee')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-muted)' }}>
            {t('employees.deleteConfirm', { name: deleteConfirm?.fullName })}
          </Typography>
          {deleteConfirm && deleteConfirm._count.assignments > 0 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
              {t('employees.deleteWarning', { count: deleteConfirm._count.assignments })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!!deleteConfirm && deleteConfirm._count.assignments > 0}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dismiss confirmation dialog */}
      <Dialog open={!!dismissConfirm} onClose={() => setDismissConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('employees.dismissEmployee')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-muted)' }}>
            {t('employees.dismissConfirm', { name: dismissConfirm?.fullName })}
          </Typography>
          {dismissConfirm && dismissConfirm._count.assignments > 0 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
              {t('employees.dismissWarning', { count: dismissConfirm._count.assignments })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissConfirm(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDismiss}
            disabled={!!dismissConfirm && dismissConfirm._count.assignments > 0}
          >
            {t('employees.dismissButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}